import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const features = [
  {
    title: 'Fortnightly rent tracking',
    desc: 'Auto-calculates every due date from your lease start date. Handles partial payments, overpayments, and arrears automatically.',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />,
  },
  {
    title: 'Tenant portal',
    desc: 'Your tenant gets their own login to check their balance and payment history. No more "how much do I owe?" texts.',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />,
  },
  {
    title: 'SMS arrears reminders',
    desc: 'Automatic SMS sent to your tenant when rent is overdue — at 7, 14, and 21 days. Set it once, forget it.',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />,
  },
  {
    title: 'Smart lease setup',
    desc: 'Upload your lease PDF and let RentFriendly read the tenant name, rent amount, and start date for you. No manual data entry.',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />,
  },
  {
    title: 'Expense tracking',
    desc: 'Log repairs, rates, insurance, and agent fees by property. Download a CSV at tax time for your accountant.',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />,
  },
  {
    title: 'Inspections & maintenance',
    desc: 'Log routine inspections and maintenance requests in one place. Know what was done and when.',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />,
  },
];

const steps = [
  { num: '1', title: 'Add your property', desc: 'Enter the address and upload your lease — RentFriendly fills in the details for you.' },
  { num: '2', title: 'Invite your tenant', desc: 'Send a one-click invite. Your tenant sets up their own password.' },
  { num: '3', title: 'Track from day one', desc: 'Record payments as they come in. Arrears reminders handle themselves.' },
];

const faqs = [
  {
    q: 'Do I need an agent?',
    a: 'No — RentFriendly is built for self-managing landlords who handle their own properties without a property manager.',
  },
  {
    q: 'Does it work for weekly rent too?',
    a: 'Currently optimised for fortnightly rent, which is the most common schedule in Australia. Weekly support is on the roadmap.',
  },
  {
    q: 'Is my data safe?',
    a: 'Yes. All data is stored securely and never shared. Tenant access is read-only — they can only see their own ledger.',
  },
  {
    q: 'Can I manage multiple properties?',
    a: 'Yes — add as many properties as you like under one landlord account.',
  },
  {
    q: 'What about tax time?',
    a: 'Use the expense tracker to log all property costs throughout the year, then download a CSV filtered by financial year for your accountant.',
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between py-4 text-left gap-4"
      >
        <span className="text-sm font-semibold text-slate-800">{q}</span>
        <svg className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <p className="text-sm text-slate-500 pb-4 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">

      {/* Nav */}
      <nav className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="h-0.5 bg-brand-600 w-full absolute top-0 left-0" />
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-14 mt-0.5">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-brand-600 rounded-lg flex items-center justify-center shadow-sm">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <span className="font-bold text-slate-800 text-[15px] tracking-tight">
              Rent<span className="text-brand-600">Friendly</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"
              className="text-sm font-medium text-slate-600 hover:text-slate-800 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors">
              Sign in
            </Link>
            <Link to="/register"
              className="text-sm bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors shadow-sm">
              Get started free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-gradient-to-b from-slate-50 to-white pt-20 pb-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-1.5 bg-brand-50 border border-brand-100 text-brand-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
            <span className="w-1.5 h-1.5 bg-brand-500 rounded-full" />
            Built for Australian self-managing landlords
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight tracking-tight mb-5">
            Rent tracking that<br />
            <span className="text-brand-600">actually makes sense</span>
          </h1>
          <p className="text-lg text-slate-500 mb-10 leading-relaxed max-w-xl mx-auto">
            Track fortnightly rent, record payments, and share a clear ledger with your tenant.
            No spreadsheets, no property manager, no confusion.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/register"
              className="w-full sm:w-auto bg-brand-600 hover:bg-brand-700 text-white px-7 py-3 rounded-xl font-semibold text-sm transition-colors shadow-sm">
              Start free — I'm a landlord
            </Link>
            <Link to="/tenant/login"
              className="w-full sm:w-auto bg-white border border-slate-300 hover:border-slate-400 text-slate-700 px-7 py-3 rounded-xl font-semibold text-sm transition-colors">
              View my rent — I'm a tenant
            </Link>
          </div>
          <p className="text-xs text-slate-400 mt-4">Free to get started · No credit card required</p>
        </div>
      </div>

      {/* How it works */}
      <div className="border-t border-slate-200 py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-2 tracking-tight">Up and running in minutes</h2>
          <p className="text-sm text-slate-500 text-center mb-10">Three steps and you're done.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {steps.map(s => (
              <div key={s.num} className="flex flex-col items-center text-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {s.num}
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{s.title}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="border-t border-slate-200 bg-slate-50 py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-2 tracking-tight">Everything you need. Nothing you don't.</h2>
          <p className="text-sm text-slate-500 text-center mb-10">Built specifically for the way Australian landlords manage property.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map(f => (
              <div key={f.title} className="flex flex-col gap-3 p-5 rounded-xl border border-slate-200 bg-white hover:border-brand-200 hover:shadow-sm transition-all">
                <div className="w-9 h-9 bg-brand-50 border border-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {f.icon}
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{f.title}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="border-t border-slate-200 bg-white py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-2 tracking-tight">Simple pricing</h2>
          <p className="text-sm text-slate-500 text-center mb-10">Start free. Upgrade when you're ready.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">

            {/* Free */}
            <div className="border border-slate-200 rounded-2xl p-7 flex flex-col">
              <div className="mb-5">
                <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Free</p>
                <div className="flex items-end gap-1">
                  <span className="text-4xl font-bold text-slate-900">$0</span>
                  <span className="text-slate-400 text-sm mb-1.5">/month</span>
                </div>
                <p className="text-xs text-slate-500 mt-2">Perfect for a single property.</p>
              </div>
              <ul className="space-y-2.5 mb-7 flex-1">
                {['1 property', 'Fortnightly rent tracking', 'Tenant portal', 'Payment ledger', 'Expense tracking'].map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm text-slate-600">
                    <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/register"
                className="w-full text-center border border-slate-300 hover:border-brand-400 text-slate-700 hover:text-brand-600 px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors">
                Get started free
              </Link>
            </div>

            {/* Pro */}
            <div className="border-2 border-brand-600 rounded-2xl p-7 flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-brand-600 text-white text-xs font-bold px-3 py-1 rounded-full">Most popular</span>
              </div>
              <div className="mb-5">
                <p className="text-sm font-semibold text-brand-600 uppercase tracking-wide mb-1">Pro</p>
                <div className="flex items-end gap-1">
                  <span className="text-4xl font-bold text-slate-900">$12</span>
                  <span className="text-slate-400 text-sm mb-1.5">/month</span>
                </div>
                <p className="text-xs text-slate-500 mt-2">For landlords with multiple properties.</p>
              </div>
              <ul className="space-y-2.5 mb-7 flex-1">
                {[
                  'Unlimited properties',
                  'Everything in Free',
                  'Smart lease setup',
                  'SMS arrears reminders',
                  'Inspection & maintenance logs',
                  'Expense CSV export',
                  'Priority support',
                ].map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm text-slate-600">
                    <svg className="w-4 h-4 text-brand-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/register"
                className="w-full text-center bg-brand-600 hover:bg-brand-700 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors shadow-sm">
                Start free trial
              </Link>
            </div>

          </div>
          <p className="text-center text-xs text-slate-400 mt-6">All prices in AUD · Cancel anytime</p>
        </div>
      </div>

      {/* FAQ */}
      <div className="border-t border-slate-200 bg-slate-50 py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-10 tracking-tight">Common questions</h2>
          <div className="bg-white rounded-2xl border border-slate-200 px-6">
            {faqs.map(f => <FaqItem key={f.q} q={f.q} a={f.a} />)}
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="border-t border-slate-200 bg-brand-600 py-14 px-6">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-3 tracking-tight">Ready to get started?</h2>
          <p className="text-brand-200 text-sm mb-7">Join landlords using RentFriendly to stay on top of their rent.</p>
          <Link to="/register"
            className="inline-block bg-white text-brand-600 hover:bg-brand-50 px-8 py-3 rounded-xl font-bold text-sm transition-colors shadow-sm">
            Create your free account
          </Link>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-200 bg-white py-6">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span className="text-xs text-slate-400">&copy; {new Date().getFullYear()} RentFriendly. Built for Australian landlords.</span>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Landlord sign in</Link>
            <Link to="/tenant/login" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Tenant sign in</Link>
          </div>
        </div>
      </div>

    </div>
  );
}
